import { useId, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  IconButton,
  Modal,
  TextInput,
  Typography,
  XMarkIcon
} from "@dealer-center/core-components";
import { Locale } from "@dealer-center/ddc-core-utils";
import { SURFACE_MUTED, SURFACE_SUBDUED } from "../layoutTokens";
import { useUserInfoArea } from "../userInfoAreaContext";
import {
  PASSWORD_SPECIAL_CHARACTERS,
  validatePassword
} from "./passwordPolicy";

export interface ResetPasswordDialogProps {
  onClose: () => void;
  onConfirm: (password: string) => void;
  /** A reset is in flight: disables confirm and both fields; Cancel and dismiss stay available. */
  isSaving?: boolean;
  /** A failed reset's message, shown below the fields; the dialog stays open for a retry. */
  errorMessage?: string;
}

const RULE_KEYS = [
  "PW_RULE_UPPER",
  "PW_RULE_LOWER",
  "PW_RULE_DIGIT",
  "PW_RULE_SPECIAL"
];

/**
 * The open reset-password dialog: rules first, then two masked fields (FR-017a, FR-017c, FR-022).
 *
 * **This component only ever exists while the dialog is open.** `ResetPasswordModal` renders it
 * conditionally, so closing the dialog unmounts it and destroys the state holding the password value.
 * That is why the password lives here rather than in `ResetPasswordModal`, and it is what makes
 * FR-017b ("dismissing discards what was typed") and FR-051 ("closing clears it from the page") true
 * by construction rather than by remembering to clear state in every close handler.
 *
 * ⚠️ **An early `return null` would NOT have achieved this.** Returning `null` removes a component's
 * DOM output but keeps its fiber — and therefore its `useState` — alive, so a single component gated
 * on an `isOpen` prop still holds the old password when reopened. That was a real defect here, caught
 * only by the "presents empty fields when reopened" test; the DOM-level assertions all passed, because
 * the value was in React state rather than in the document. The split into a gate plus this component
 * is load-bearing, not organisational.
 *
 * **No `maxlength` on either field.** It counts UTF-16 code units and cannot express FR-022a's byte
 * bound, so it would truncate a valid accented password at the wrong point, silently.
 *
 * The soft-disabled mode (`isWriteAvailable={false}`, 2026-08-27, for while no write endpoint
 * existed) was removed once both pages opened the dialog live (`specs/my-info-write`).
 *
 * `onConfirm` here does **not** close the dialog or clear the fields — it only reports the value
 * upwards. The caller closes it on success (the unmount discards the password) and keeps it open
 * with `errorMessage` on failure.
 */
export const ResetPasswordDialog = ({
  onClose,
  onConfirm,
  isSaving = false,
  errorMessage
}: ResetPasswordDialogProps) => {
  // Telemetry attribution only — see `userInfoAreaContext`.
  const area = useUserInfoArea();

  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const rulesId = useId();

  const result = validatePassword(password, confirmation);
  const hasTyped = password !== "" || confirmation !== "";
  // FR-022: a rule violation and a mismatch are reported distinctly, and can both show at once.
  const isShowingRuleError = hasTyped && !result.isPolicyMet;
  const isShowingMatchError = hasTyped && result.isMismatched;

  const rulesBlock = (
    /*
     * A subdued panel, not plain text on the dialog's white (the prototype's `#pwRules`). The policy
     * is reference material the user reads once and then works against, so it is set apart from the
     * fields rather than reading as the first thing in a continuous block of prose.
     */
    <Box
      data-testid={"password-rules"}
      id={rulesId}
      sx={{
        backgroundColor: SURFACE_SUBDUED,
        borderRadius: "4px",
        padding: "10px 12px"
      }}
    >
      <Typography
        color={"sem.color.on-surface.subdued"}
        tag={"p"}
        variant={"body-sm"}
      >
        {Locale.getText("PW_RULES") as string}
      </Typography>
      <Box component={"ul"} sx={{ marginTop: "6px", paddingLeft: "20px" }}>
        {RULE_KEYS.map(key => (
          <Box component={"li"} key={key}>
            <Typography
              color={"sem.color.on-surface.subdued"}
              tag={"span"}
              variant={"body-sm"}
            >
              {Locale.getText(key) as string}
            </Typography>
          </Box>
        ))}
      </Box>

      {/*
       * FR-017c: the full 32-mark set must be reachable **without leaving the dialog**, but revealed on
       * demand rather than inline — 32 punctuation marks would otherwise dominate the dialog. An
       * `Accordion` is used because it is the only sanctioned component that renders a disclosure
       * conveying `aria-expanded`; Interstate's `Button` exposes no such prop. Collapsed on every open,
       * which follows from this component being mounted fresh each time.
       *
       * The summary keeps one fixed label rather than swapping to a "Hide…" wording, because
       * `aria-expanded` already conveys the state and a label that changes under the user is the
       * thing screen-reader users have to re-read. (The prototype's `PW_CHARSET_HIDE` is therefore
       * deliberately not carried over.)
       */}
      <Accordion data-testid={"password-charset-disclosure"}>
        {/*
         * ⚠️ **`whiteSpace: "normal"` is load-bearing, and it overrides an Interstate default.**
         *
         * `accordionSummaryDefaultStyle` hard-codes `whiteSpace: 'nowrap'` with `overflow: 'hidden'`
         * on the inner `MuiAccordionSummary` (plus `textWrap: 'ellipsis'`, which is not a valid
         * value for that property — so there is not even an ellipsis to mark the truncation). The
         * result is that **"View accepted special characters" is clipped** at any width where the
         * panel is narrower than the label.
         *
         * **Measured at a 390px viewport** (`ResetPasswordDialog.cy.tsx`'s mobile case):
         *
         * | | `.MuiAccordionSummary-root` scrollWidth / clientWidth |
         * |---|---|
         * | Interstate's default | **260 / 254** — six pixels cut off |
         * | with this override | 239 / 239 — wrapped, nothing clipped |
         *
         * **What this does NOT fix, stated precisely because the surrounding code has a history of
         * over-claiming here:** it does not change the dialog's width and it did not cause a
         * horizontally-scrolling dialog. The paper measures **326px either way** at 390px, and the
         * document's `scrollWidth` equals its `clientWidth` in both cases. The `nowrap` is a
         * **clipping** bug, not a sizing one. See the `sx` note on `Modal` below for the separate,
         * still-unreproduced width report — this is not it.
         *
         * Applied unconditionally rather than below a breakpoint: at 460px the label already fits on
         * one line, so allowing it to wrap changes nothing at desktop. `overflow: "visible"` goes
         * with it — once the label wraps there is nothing to clip, and leaving `hidden` would cut off
         * the second line instead.
         */}
        <AccordionSummary
          data-testid={"password-charset-summary"}
          sx={{ overflow: "visible", whiteSpace: "normal" }}
        >
          <Typography tag={"span"} variant={"body-md"}>
            {Locale.getText("PW_CHARSET_SHOW") as string}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box data-testid={"password-charset"}>
            {/*
             * ⚠️ **`overflowWrap: "anywhere"` is load-bearing — this row is the dialog's widest
             * child and the reason its width was content-driven.**
             *
             * 32 punctuation marks joined by spaces looks freely wrappable, but a browser will not
             * break every one of those runs: sequences like `` [ \ ] ^ _ ` `` are treated as a unit
             * for line-breaking purposes, so the row demanded ~365px of unbreakable inline content
             * and the `width: auto` paper simply grew to fit it (see the `sx` note below). With the
             * width now fixed at 460px this row would instead overflow *inside* the dialog and give
             * it a horizontal scrollbar, so it has to be able to break anywhere.
             *
             * `anywhere` rather than `break-word`: both break a long run, but `anywhere` also counts
             * the break opportunities when computing the element's **min-content width**, which is
             * what stops the row from influencing the layout in the first place.
             */}
            <Typography
              sx={{ overflowWrap: "anywhere" }}
              tag={"p"}
              variant={"body-sm"}
            >
              {PASSWORD_SPECIAL_CHARACTERS.split("").join(" ")}
            </Typography>
            {/* FR-022b: the set says what *satisfies* the rule, not what is permitted. */}
            <Typography
              color={"sem.color.on-surface.muted"}
              tag={"p"}
              variant={"body-xs"}
            >
              {Locale.getText("PW_CHARSET_NOTE") as string}
            </Typography>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );

  return (
    <Modal
      show
      data-testid={"reset-password-modal"}
      footer={{
        footerComponent: (
          /*
           * The prototype's `.modal-foot`: right-aligned, gapped, padded, with a rule above it.
           *
           * All four matter and none are decoration. Without `flex-end` the pair rendered hard against
           * the dialog's left edge, reading as body content rather than as the dialog's actions.
           * Without the padding the disabled primary's grey fill ran into the dialog's bottom-right
           * corner. The rule separates the actions from the last field, which otherwise sat flush
           * against them. Interstate's own `actionsPadding` does not apply here — passing
           * `footerComponent` means this component owns the footer's layout.
           */
          <Box
            sx={{
              borderTop: `1px solid ${SURFACE_MUTED}`,
              columnGap: "8px",
              display: "flex",
              justifyContent: "flex-end",
              padding: "16px 24px",
              width: "100%"
            }}
          >
            <Button
              analyticsConfiguration={{
                eventName: `${area.slug}-reset-password-cancel`,
                eventProperties: undefined
              }}
              buttonStyle={"tertiary"}
              data-testid={"password-cancel"}
              onClick={onClose}
            >
              {Locale.getText("CANCEL") as string}
            </Button>
            <Button
              analyticsConfiguration={{
                eventName: `${area.slug}-reset-password-confirm`,
                eventProperties: undefined
              }}
              buttonStyle={"primary"}
              data-testid={"password-confirm"}
              // FR-017a: inactive until both fields are filled **and** valid. `isValid` covers the
              // policy and the match; `hasTyped` covers "filled", since two empty fields are neither
              // a mismatch nor filled. Also inactive while a reset is in flight.
              disabled={isSaving || !hasTyped || !result.isValid}
              onClick={() => onConfirm(password)}
            >
              {
                Locale.getText(
                  isSaving ? "UM_PW_RESETTING" : "RESET_PASSWORD"
                ) as string
              }
            </Button>
          </Box>
        )
      }}
      header={
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            justifyContent: "space-between",
            width: "100%"
          }}
        >
          <Typography tag={"h2"} variant={"h5"}>
            {Locale.getText("RESET_PASSWORD") as string}
          </Typography>
          {/*
           * FR-017a's dismiss control, distinct from Cancel. Interstate's `Modal` renders one of its
           * own, but **only at `size="large"`** (`displayCloseButton: size === 'large'`) — and it is a
           * `Box` with an `onClick`, so it is neither focusable nor announced. This dialog is `small`,
           * so the control has to be supplied here.
           *
           * `aria-label` is required, not decoration: an `IconButton` whose only child is an icon has
           * no accessible name otherwise, and FR-050 requires every control to be announced as such.
           * The icon's `title` is a **required** prop on the core-components wrapper — omitting it is
           * a `typecheck`-only failure, exactly like `Button`'s `analyticsConfiguration` — so both
           * read the same key rather than being left free to drift apart.
           *
           * Note that `IconButton` also **prefixes** `data-testid` into
           * `ids-icon-button-password-dismiss` (the same `idBuilder` treatment `CheckBox` gives its
           * root), which is why this is asserted by role and name rather than by test id.
           */}
          <IconButton
            aria-label={Locale.getText("CLOSE") as string}
            buttonStyle={"tertiary"}
            data-testid={"password-dismiss"}
            size={"sm"}
            onClick={onClose}
          >
            <XMarkIcon title={Locale.getText("CLOSE") as string} />
          </IconButton>
        </Box>
      }
      /*
       * The prototype draws this dialog at **460px** and Interstate's scale has no step there: `small`
       * caps at 320px, `large` at 640px (`modalDefaultStyle.js`). 320px is not merely tighter than the
       * mock, it is too narrow for this content — "View accepted special characters" is ~2px wider
       * than the panel's inner width, so the disclosure label was clipped mid-word, and the extra
       * wrapping pushed the dialog past the viewport into a scroll.
       *
       * `size="large"` is not the answer either: Interstate renders its own close button at `large`
       * only (`displayCloseButton: size === 'large'`), and that control is a `Box` with an `onClick`,
       * so it is neither focusable nor announced. Taking it would mean either two dismiss controls or
       * trading this dialog's accessible one for an inaccessible one.
       *
       * So `small` is widened to the mock's 460px, targeting **`.MuiDialog-paper` specifically**. The
       * first attempt used `& > *`, which also caught the backdrop — it shrank to 460px and stopped
       * covering the viewport, and the dialog lost its centring. The paper is the one element
       * carrying `maxWidth` from the size map, so it is the only one to override.
       *
       * ⚠️ **`width` is overridden as well as `maxWidth`.** Interstate's `small` is `width: auto`, so
       * raising `maxWidth` alone left the paper **content-sized** — it rendered at whatever its
       * widest child demanded, merely *capped* at 460. `width: 100%` makes the width
       * **deterministic**: the paper fills its container up to the 460px cap regardless of what the
       * content wants, so the content wraps instead of setting the size.
       *
       * **Status, recorded honestly (2026-09-15):** this and the charset row's `overflowWrap` were
       * made in response to a report of a too-wide, horizontally-scrolling dialog **in the running
       * app**, and that report is **not reproduced** by `ResetPasswordDialog.cy.tsx` or by
       * `UserManager.cy.tsx`'s in-page case. Both measure the dialog clean — 460/460/460 at
       * 1440/1024/800, nothing overflowing — **with the fixes reverted as well as applied.** So
       * these two changes are *defensible hardening of a real weakness* (a fixed-width design whose
       * width was content-driven, sitting exactly on its own cap), **not a verified fix for the
       * reported symptom.** Whatever tips it over is outside what a component test reproduces — the
       * app's real webfont, the ControlCenter shell, or a viewport the specs do not cover. **Do not
       * treat this as closed** until it has been confirmed in the running app.
       */
      size={"small"}
      sx={{ "& .MuiDialog-paper": { maxWidth: "460px", width: "100%" } }}
      onHide={onClose}
    >
      {/* FR-017a: rules **before** the fields, so they are read before anything is typed. */}
      {rulesBlock}

      {/*
       * FR-050a: the rules text is programmatically associated with the fields rather than merely
       * sitting near them. The association is on a `role="group"` wrapper, not on each input, because
       * Interstate's `TextInput` exposes no `aria-describedby` — `slotProps.textInput.dataAttr` is
       * keyed `data-*` only, and `helpMessage` renders text without ever wiring `aria-describedby`.
       * Describing a group of fields that share one set of rules is the conventional pattern anyway.
       * Recorded for T068's a11y pass: field-level association is not reachable through the wrapper.
       */}
      <Box
        aria-describedby={rulesId}
        role={"group"}
        sx={{
          display: "flex",
          flexDirection: "column",
          marginTop: "16px",
          rowGap: "16px"
        }}
      >
        <TextInput
          required
          data-testid={"password-new"}
          disabled={isSaving}
          id={"myinfo-password-new"}
          label={Locale.getText("PASSWORD") as string}
          name={"password"}
          type={"password"}
          value={password}
          onChange={event => setPassword(event.target.value)}
        />
        <TextInput
          required
          data-testid={"password-confirmation"}
          disabled={isSaving}
          id={"myinfo-password-confirm"}
          label={Locale.getText("CONFIRM_PASSWORD") as string}
          name={"confirmation"}
          type={"password"}
          value={confirmation}
          onChange={event => setConfirmation(event.target.value)}
        />

        {isShowingRuleError ? (
          <Alert
            data-testid={"password-rule-error"}
            role={"alert"}
            type={"error"}
          >
            {Locale.getText("PW_ERR_RULES") as string}
          </Alert>
        ) : null}

        {isShowingMatchError ? (
          <Alert
            data-testid={"password-match-error"}
            role={"alert"}
            type={"error"}
          >
            {Locale.getText("PASSWORD_MATCH_ERROR") as string}
          </Alert>
        ) : null}

        {/* `role="alert"`: the user just caused this. */}
        {errorMessage ? (
          <Alert
            data-testid={"password-reset-error"}
            role={"alert"}
            type={"error"}
          >
            {errorMessage}
          </Alert>
        ) : null}
      </Box>
    </Modal>
  );
};
